// ============================================
// COMPONENTE: Busca Textual em Prontuários
// ============================================
// Permite pesquisar termos em todos os prontuários do paciente
// Exibe resultados destacando os trechos encontrados
// ============================================

import { useState, useCallback } from 'react';

interface Prontuario {
  id: number;
  data_consulta: string;
  historico_sessao?: string;
  observacoes?: string;
  evolucao?: string;
  plano_tratamento?: string;
  hipotese_diagnostica?: string;
  encaminhamentos?: string;
}

interface ResultadoBusca {
  prontuario_id: number;
  data_consulta: string;
  campo: string;
  trecho: string;
  posicao: number;
}

interface Props {
  prontuarios: Prontuario[];
  onVerProntuario?: (prontuarioId: number) => void;
}

export default function BuscaTextualProntuarios({ prontuarios, onVerProntuario }: Props) {
  const [termoBusca, setTermoBusca] = useState('');
  const [resultados, setResultados] = useState<ResultadoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [buscaRealizada, setBuscaRealizada] = useState(false);

  // Campos para busca com labels amigáveis
  const camposBusca: { chave: keyof Prontuario; label: string; icone: string }[] = [
    { chave: 'historico_sessao', label: 'Histórico da Sessão', icone: 'fa-file-medical' },
    { chave: 'observacoes', label: 'Observações', icone: 'fa-sticky-note' },
    { chave: 'evolucao', label: 'Evolução', icone: 'fa-chart-line' },
    { chave: 'plano_tratamento', label: 'Plano de Tratamento', icone: 'fa-clipboard-list' },
    { chave: 'hipotese_diagnostica', label: 'Hipótese Diagnóstica', icone: 'fa-diagnoses' },
    { chave: 'encaminhamentos', label: 'Encaminhamentos', icone: 'fa-external-link-alt' }
  ];

  // Extrair trecho com contexto
  const extrairTrecho = (texto: string, termo: string, tamanhoContexto: number = 100): { trecho: string; posicao: number } => {
    const textoLower = texto.toLowerCase();
    const termoLower = termo.toLowerCase();
    const posicao = textoLower.indexOf(termoLower);
    
    if (posicao === -1) {
      return { trecho: '', posicao: -1 };
    }

    const inicio = Math.max(0, posicao - tamanhoContexto);
    const fim = Math.min(texto.length, posicao + termo.length + tamanhoContexto);
    
    let trecho = texto.substring(inicio, fim);
    
    // Adicionar reticências se cortou
    if (inicio > 0) trecho = '...' + trecho;
    if (fim < texto.length) trecho = trecho + '...';

    return { trecho, posicao };
  };

  // Destacar termo no texto
  const destacarTermo = (texto: string, termo: string): JSX.Element => {
    if (!termo) return <>{texto}</>;
    
    const regex = new RegExp(`(${termo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const partes = texto.split(regex);
    
    return (
      <>
        {partes.map((parte, i) => 
          regex.test(parte) ? (
            <mark key={i} className="bg-warning px-1 rounded">{parte}</mark>
          ) : (
            <span key={i}>{parte}</span>
          )
        )}
      </>
    );
  };

  // Realizar busca
  const realizarBusca = useCallback(() => {
    if (!termoBusca.trim() || termoBusca.length < 2) {
      alert('Digite pelo menos 2 caracteres para buscar');
      return;
    }

    setBuscando(true);
    setBuscaRealizada(true);
    const resultadosEncontrados: ResultadoBusca[] = [];

    prontuarios.forEach(prontuario => {
      camposBusca.forEach(({ chave, label }) => {
        const conteudo = prontuario[chave] as string | undefined;
        if (conteudo && typeof conteudo === 'string') {
          const { trecho, posicao } = extrairTrecho(conteudo, termoBusca);
          if (posicao !== -1) {
            resultadosEncontrados.push({
              prontuario_id: prontuario.id,
              data_consulta: prontuario.data_consulta,
              campo: label,
              trecho,
              posicao
            });
          }
        }
      });
    });

    // Ordenar por data mais recente
    resultadosEncontrados.sort((a, b) => 
      new Date(b.data_consulta).getTime() - new Date(a.data_consulta).getTime()
    );

    setResultados(resultadosEncontrados);
    setBuscando(false);
  }, [termoBusca, prontuarios]);

  // Limpar busca
  const limparBusca = () => {
    setTermoBusca('');
    setResultados([]);
    setBuscaRealizada(false);
  };

  // Formatar data
  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Agrupar resultados por prontuário
  const resultadosAgrupados = resultados.reduce((acc, resultado) => {
    const key = resultado.prontuario_id;
    if (!acc[key]) {
      acc[key] = {
        prontuario_id: resultado.prontuario_id,
        data_consulta: resultado.data_consulta,
        ocorrencias: []
      };
    }
    acc[key].ocorrencias.push({
      campo: resultado.campo,
      trecho: resultado.trecho
    });
    return acc;
  }, {} as Record<number, { prontuario_id: number; data_consulta: string; ocorrencias: Array<{ campo: string; trecho: string }> }>);

  return (
    <div className="card card-outline card-primary">
      <div className="card-header">
        <h3 className="card-title">
          <i className="fas fa-search me-2"></i>
          Busca Textual em Prontuários
        </h3>
      </div>
      <div className="card-body">
        {/* Campo de Busca */}
        <div className="input-group mb-3">
          <span className="input-group-text">
            <i className="fas fa-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Digite um termo para buscar em todos os prontuários (ex: pai, ansiedade, trabalho...)"
            value={termoBusca}
            onChange={(e) => setTermoBusca(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && realizarBusca()}
          />
          <button
            className="btn btn-primary"
            onClick={realizarBusca}
            disabled={buscando || termoBusca.length < 2}
          >
            {buscando ? (
              <><i className="fas fa-spinner fa-spin me-1"></i> Buscando...</>
            ) : (
              <><i className="fas fa-search me-1"></i> Buscar</>
            )}
          </button>
          {buscaRealizada && (
            <button
              className="btn btn-outline-secondary"
              onClick={limparBusca}
              title="Limpar busca"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Info de campos pesquisados */}
        <small className="text-muted d-block mb-3">
          <i className="fas fa-info-circle me-1"></i>
          A busca é realizada nos campos: {camposBusca.map(c => c.label).join(', ')}
        </small>

        {/* Resultados */}
        {buscaRealizada && (
          <>
            {/* Estatísticas */}
            <div className="alert alert-info py-2">
              <i className="fas fa-chart-bar me-2"></i>
              <strong>{resultados.length}</strong> ocorrência(s) encontrada(s) 
              em <strong>{Object.keys(resultadosAgrupados).length}</strong> prontuário(s) 
              de <strong>{prontuarios.length}</strong> total
            </div>

            {/* Lista de resultados */}
            {resultados.length === 0 ? (
              <div className="text-center py-4">
                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                <p className="text-muted">
                  Nenhum resultado encontrado para "<strong>{termoBusca}</strong>"
                </p>
                <small className="text-muted">
                  Tente usar termos diferentes ou verificar a ortografia
                </small>
              </div>
            ) : (
              <div className="timeline">
                {Object.values(resultadosAgrupados).map((grupo) => (
                  <div key={grupo.prontuario_id} className="card mb-3 border-left-primary" style={{ borderLeft: '4px solid #007bff' }}>
                    <div className="card-header bg-light d-flex justify-content-between align-items-center py-2">
                      <div>
                        <i className="fas fa-calendar-alt me-2 text-primary"></i>
                        <strong>{formatarData(grupo.data_consulta)}</strong>
                        <span className="badge bg-primary ms-2">
                          {grupo.ocorrencias.length} ocorrência(s)
                        </span>
                      </div>
                      {onVerProntuario && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onVerProntuario(grupo.prontuario_id)}
                        >
                          <i className="fas fa-eye me-1"></i>
                          Ver Prontuário
                        </button>
                      )}
                    </div>
                    <div className="card-body py-2">
                      {grupo.ocorrencias.map((ocorrencia, idx) => (
                        <div key={idx} className={idx > 0 ? 'mt-2 pt-2 border-top' : ''}>
                          <small className="text-muted d-block mb-1">
                            <i className="fas fa-tag me-1"></i>
                            {ocorrencia.campo}:
                          </small>
                          <p className="mb-0 small" style={{ lineHeight: '1.6' }}>
                            "{destacarTermo(ocorrencia.trecho, termoBusca)}"
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Estado inicial */}
        {!buscaRealizada && (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-lightbulb fa-3x mb-3"></i>
            <p>
              Pesquise por palavras-chave para encontrar padrões e temas recorrentes nos prontuários
            </p>
            <div className="row justify-content-center">
              <div className="col-auto">
                <small>
                  <strong>Sugestões:</strong> família, trabalho, ansiedade, sono, medicação, pai, mãe, infância
                </small>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
